// Date/formatting helpers for the quarterly review period picker. These are
// pure logic (not data) — unlike mockData.js (removed), nothing here is a
// placeholder for a backend response.
import moment from "moment";

export const REVIEW_YEAR = new Date().getFullYear();

export const QUARTERS = [
  { label: `Q1 ${REVIEW_YEAR}`, sub: "Jan 1 – Mar 31", from: `${REVIEW_YEAR}-01-01`, to: `${REVIEW_YEAR}-03-31` },
  { label: `Q2 ${REVIEW_YEAR}`, sub: "Apr 1 – Jun 30", from: `${REVIEW_YEAR}-04-01`, to: `${REVIEW_YEAR}-06-30` },
  { label: `Q3 ${REVIEW_YEAR}`, sub: "Jul 1 – Sep 30", from: `${REVIEW_YEAR}-07-01`, to: `${REVIEW_YEAR}-09-30` },
  { label: `Q4 ${REVIEW_YEAR}`, sub: "Oct 1 – Dec 31", from: `${REVIEW_YEAR}-10-01`, to: `${REVIEW_YEAR}-12-31` },
];

export const formatRange = (from, to) => {
  if (!from || !to) return "";
  const f = moment(from);
  const t = moment(to);
  if (f.year() === t.year()) {
    return `${f.format("MMM D")} – ${t.format("MMM D, YYYY")}`;
  }
  return `${f.format("MMM D, YYYY")} – ${t.format("MMM D, YYYY")}`;
};

// `quarters` defaults to the static calendar-year fallback — callers with a
// live preset list (e.g. RangePickerField, once quarter-presets has loaded)
// pass that instead so a match reflects whatever "Quarter basis" setting is
// actually active (calendar vs. ISP-plan-year anchored).
export const matchQuarter = (from, to, quarters = QUARTERS) =>
  quarters.findIndex((q) => moment(q.from).isSame(from, "day") && moment(q.to).isSame(to, "day"));

export const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

// Mirrors ogiso-backend/services/quarterlyPcr.service.js's own
// CHECKLIST_TEMPLATE/QUESTIONS_TEMPLATE (minus the server-only `keywords`
// used for note-matching during AI drafting) — a manual report is never
// created on the server until the reviewer's first Save, so the blank
// skeleton they see meanwhile has to be built here instead of fetched.
export const CHECKLIST_TEMPLATE = [
  { id: "medication", label: "Medication use" },
  { id: "meals", label: "Meal planning / preparation / intake" },
  { id: "bathing", label: "Bathing" },
  { id: "communication", label: "Communication support" },
  { id: "transportation", label: "Transportation" },
  { id: "crisis", label: "Crisis plan" },
];

// DBHDS ISP v4.0 Section C, in its required wording.
export const QUESTIONS_TEMPLATE = [
  {
    id: "q1",
    text: "For the reporting period have there been any safety risks (health or behavioral) identified?",
  },
  {
    id: "q2",
    text: "Does the person or substitute decision-maker desire and/or need any changes to the plan or services?",
  },
  { id: "q3", text: "Is the person and substitute decision-maker satisfied with all services and supports?" },
  { id: "q4", text: "Were all Medicaid services in the plan implemented?" },
  { id: "q5", text: "Were there any significant events (health or otherwise) not reported above?" },
];

// Same default the server falls back to in createManualReport() when no
// period is supplied — the prior full quarter.
export const getPreviousQuarterRange = () => {
  const currentQuarterStart = moment().startOf("quarter");
  const start = currentQuarterStart.clone().subtract(1, "quarter").startOf("day");
  const end = currentQuarterStart.clone().subtract(1, "day").endOf("day");
  return { from: start.toDate(), to: end.toDate() };
};

// A blank, unsaved manual report — same shape as the server's toFullReport(),
// with `id: null` marking it as not yet created. Nothing here is persisted
// until the reviewer's first Save (see handleSaveDraft/handleSaveNext).
export const blankManualDraft = (providerId, providerName) => {
  const period = getPreviousQuarterRange();
  return {
    id: null,
    clientId: null,
    providerId: providerId || null,
    facilityId: null,
    clientName: "",
    providerName: providerName || "",
    facilityName: "",
    periodStart: period.from,
    periodEnd: period.to,
    periodLabel: formatRange(period.from, period.to),
    status: "draft",
    source: "manual",
    evidenceFiles: [],
    createdAt: null,
    outcomesA: [
      {
        id: "outcome-1",
        title: "",
        meta: "",
        noteCount: 0,
        status: null,
        description: "",
        planChangeNeeded: null,
        planChangeDescription: "",
      },
    ],
    risksB: [
      {
        id: "support-1",
        title: "",
        meta: "",
        noteCount: 0,
        riskType: null,
        status: null,
        description: "",
        planChangeNeeded: null,
        planChangeDescription: "",
      },
    ],
    supportB: null,
    routineSupportsB: [],
    checklistB: CHECKLIST_TEMPLATE.map((item) => ({
      id: item.id,
      label: item.label,
      checked: false,
      note: "",
      fromPlan: false,
    })),
    questionsC: QUESTIONS_TEMPLATE.map((q) => ({ id: q.id, text: q.text, answer: null, note: "" })),
    flags: [],
    requiresReview: false,
    flagsAcknowledged: null,
    ispRevisionDates: "",
    signature: null,
  };
};
